class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this._rebindHeader = () => this.setHeaderCartIconAccessibility();
    document.addEventListener('shopify:section:load', this._rebindHeader);
    document.addEventListener('cart:refresh', this._rebindHeader);

    // ── FIX: Cart-Drawer zeigt nach Seitenwechsel teils keine Waren an, obwohl
    // cart.item_count > 0. Ursache: Server-side gerenderte Seite wird aus dem
    // Browser-/CDN-Cache geladen, dort war der Warenkorb noch leer.
    // Lösung: Beim Initialisieren echten Cart-Stand per /cart.js prüfen und
    // den Drawer-Inhalt bei Mismatch via Section-API neu laden.
    this.syncWithServerCart();
  }

  syncWithServerCart() {
    if (this._cartSynced) return;
    this._cartSynced = true;
    const cartBase = (window.routes && window.routes.cart_url) || '/cart';
    // Echten Cart-Stand laden, NUR is-empty-Klassen und cart-items-wrapper
    // abgleichen — KEIN destruktives DOM-Replace, sonst Race-Condition mit
    // CartItems.updateQuantity (führt zum "Beim Aktualisieren …"-Fehler).
    // Wenn die Items im Server-HTML wirklich fehlen, lädt erst onCartUpdate
    // (in cart.js) den frischen Inhalt sauber nach.
    fetch(`${cartBase}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        const itemRows = this.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
        const isEmpty  = cart.item_count === 0;

        // is-empty-Klassen abgleichen (häufiger Server-Cache-Bug)
        this.classList.toggle('is-empty', isEmpty);
        const inner = this.querySelector('.drawer__inner');
        if (inner) inner.classList.toggle('is-empty', isEmpty);

        // Wenn Cart laut API Items hat, aber DOM zeigt 0 → still neu laden.
        // Zustand prüfen: Drawer darf NICHT offen sein und KEIN Update
        // gerade laufen.
        if (cart.item_count > 0 && itemRows === 0 && !this.classList.contains('active')) {
          fetch(`${cartBase}?section_id=cart-drawer`)
            .then((r) => r.text())
            .then((txt) => {
              if (this.classList.contains('active')) return;        // wurde inzwischen geöffnet
              if (this.querySelectorAll('#CartDrawer-CartItems .cart-item').length > 0) return; // wurde inzwischen befüllt
              const doc       = new DOMParser().parseFromString(txt, 'text/html');
              const freshList = doc.querySelector('#CartDrawer-CartItems');
              const targetList = this.querySelector('#CartDrawer-CartItems');
              if (freshList && targetList) targetList.innerHTML = freshList.innerHTML;
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }

  setHeaderCartIconAccessibility() {
    // Globale Erfassung ALLER /cart-Links — der User möchte ausschließlich
    // den Drawer benutzen, nie die /cart-Page sehen.
    //
    // Erfasst werden:
    //   - das Cart-Icon im Header
    //   - jeder Link mit href="/cart" oder href endet auf /cart
    //   - explizit markierte Trigger ([data-kk-cart], .kk-cart)
    //
    // Ausgeschlossen (über data-no-drawer="true"): Stellen, an denen man
    // wirklich zur Cart-Page möchte, z. B. Theme-interne Updates.
    const triggers = document.querySelectorAll(
      '#cart-icon-bubble, [data-kk-cart], ' +
      'a[href="/cart"], a[href$="/cart"], a[href$="/cart/"], ' +
      'a[href="' + ((window.routes && window.routes.cart_url) || '/cart') + '"]'
    );
    if (!triggers.length) return;

    triggers.forEach((cartLink) => {
      if (cartLink.dataset.cartDrawerBound === '1') return;
      if (cartLink.dataset.noDrawer === 'true') return;          // Opt-out
      cartLink.dataset.cartDrawerBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        // Auf /cart selbst nicht abfangen (falls jemand doch dort landet,
        // soll der Reload weiterhin funktionieren).
        if (window.location.pathname.replace(/\/$/, '') === '/cart') return;
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        const code = event.code.toUpperCase();
        if (code === 'SPACE' || code === 'ENTER') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // ── FIX: remove is-empty from the <cart-drawer> element itself.
    // The original code only removed it from .drawer__inner, but all
    // CSS visibility rules are scoped to cart-drawer.is-empty (the
    // custom element), so the drawer stayed in empty-state layout
    // even after items were added on a fresh page load.
    this.classList.remove('is-empty');

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) return;
      try {
        sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      } catch (e) {
        console.warn('cart-drawer renderContents skip', section.id, e);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
